import React from "react";
import { radarGeometry } from "../../ForCoaches/v2/team/teamModel";

// Player radar (the Figma note on 1504:5385, drawn like the coach Team
// Archetype phone radar 2409:1225): one spoke per stat, clockwise from the
// top, the vertex at the athlete's percentile in the peer group, rings at
// 25 / 50 / 75 / 100. One 306px plot for phones and desktop, scaled down on
// narrow phones. Plain SVG, not recharts: the profile suites mock recharts
// with a fixed list of exports.
//
// axes       a group's spokes (the profile model's `radarGroups[].radar`):
//            { key, label, value, detail, rank }
// peerLabel  who the percentiles rank against (`radarGroups[].peerLabel`)

export const RADAR_TITLE = "Player radar";
export const RADAR_EMPTY = "Not enough peer data for a comparison yet.";
// The League / College / Pro switch on the radar cards.
export const RADAR_GROUPS_LABEL = "Compare with";
export const radarCaption = (peerLabel) => `Percentile vs ${peerLabel}`;

const WIDTH = 306;
const CX = WIDTH / 2;
const RADIUS = 80;
const RINGS = [0.25, 0.5, 0.75, 1];
const GRID = "#E8E8EC";
const BRAND = "#FF4400";
const INK = "#2c2c2c";
const MUTED = "#757575";
// A label block sits GAP clear of its spoke's end: one bm-label-l line
// (18px) per word of the stat name, then the bm-body-s rank (16px), with
// the baselines where those line boxes put them.
const GAP = 8;
const NAME_LINE = 18;
const NAME_BASELINE = 13.7;
const RANK_LINE = 16;
const RANK_BASELINE = 12;
const PAD = 2;

// Centred above the top spoke and below the bottom one, beside the others
// and vertically centred on their ends. "True shooting" stacks a word a
// line, so every name fits beside its spoke.
const blockAt = (spoke, cy) => {
  const lines = String(spoke.factor.label).split(" ");
  const height = lines.length * NAME_LINE + RANK_LINE;
  const { x, y } = spoke.axis;
  if (Math.abs(x - CX) < 1) return { lines, height, x, top: y < cy ? y - GAP - height : y + GAP, anchor: "middle" };
  const right = x > CX;
  return { lines, height, x: right ? x + GAP : x - GAP, top: y - height / 2, anchor: right ? "start" : "end" };
};

// Laid out around y = 0 first, then moved down so the highest label starts
// PAD from the top; the plot is as tall as its labels need.
const layout = (axes) => {
  const order = axes.map((a) => a.key);
  const factors = axes.map((a) => ({ ...a, code: a.key, pct: a.value }));
  const spokesAt = (cy) => radarGeometry(factors, { cx: CX, cy, radius: RADIUS, order });
  const blocks = spokesAt(0).map((s) => blockAt(s, 0));
  const top = Math.min(-RADIUS, ...blocks.map((b) => b.top));
  const bottom = Math.max(RADIUS, ...blocks.map((b) => b.top + b.height));
  const cy = PAD - top;
  const spokes = spokesAt(cy);
  return { cy, height: bottom - top + 2 * PAD, spokes, blocks: spokes.map((s) => blockAt(s, cy)) };
};

// 1st, 2nd, 3rd, 4th, then 11th to 13th, 21st.
const ordinal = (n) => {
  const v = Math.round(n);
  const teen = v % 100 >= 11 && v % 100 <= 13;
  return `${v}${teen ? "th" : ["th", "st", "nd", "rd"][v % 10] || "th"}`;
};

const PercentileRadar = ({ axes, peerLabel }) => {
  const { cy, height, spokes, blocks } = layout(axes);
  const summary = axes.map((a) => `${a.label} ${ordinal(a.value)} percentile (${a.detail})`).join(", ");
  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${height}`}
      role="img"
      aria-label={`${RADAR_TITLE}, percentile vs ${peerLabel}: ${summary}`}
      className="h-auto w-full max-w-[306px] self-center"
    >
      {RINGS.map((r) => (
        <circle key={r} cx={CX} cy={cy} r={RADIUS * r} fill="none" stroke={GRID} />
      ))}
      {spokes.map((s) => (
        <line key={s.code} x1={CX} y1={cy} x2={s.axis.x} y2={s.axis.y} stroke={GRID} />
      ))}
      <polygon
        points={spokes.map((s) => `${s.vertex.x},${s.vertex.y}`).join(" ")}
        fill={BRAND}
        fillOpacity={0.24}
        stroke={BRAND}
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      {spokes.map((s) => (
        <circle key={`dot-${s.code}`} cx={s.vertex.x} cy={s.vertex.y} r={5} fill={BRAND} stroke="#fff" strokeWidth={2}>
          <title>{`${s.factor.label}: ${s.factor.detail}`}</title>
        </circle>
      ))}
      {spokes.map((s, i) => {
        const b = blocks[i];
        return (
          <text key={`label-${s.code}`} x={b.x} textAnchor={b.anchor}>
            {b.lines.map((line, j) => (
              <tspan key={`${line}-${j}`} x={b.x} y={b.top + j * NAME_LINE + NAME_BASELINE} className="bm-label-l" fill={INK}>
                {line}
              </tspan>
            ))}
            <tspan x={b.x} y={b.top + b.lines.length * NAME_LINE + RANK_BASELINE} className="bm-body-s" fill={MUTED}>
              {s.factor.rank}
            </tspan>
          </text>
        );
      })}
    </svg>
  );
};

export default PercentileRadar;
